<template>
    <hts-modal
        :show="visible"
        size="large"
        :type="!disabled ? 'confirm' : 'modal'"
        :title="$t('labels.tollStationInformation')"
        @hide="handleClose"
        @confirm="handleSubmit">
        <template #custom>
            <p
                v-if="!tollStationStructure"
                class="text-center bold mt-5 text-xl">
                {{ $t('labels.pleaseInstallTollStationTariffStructure') }}
            </p>
            <div v-if="tollStationStructure?.vehicle || tollStationStructure?.cont">
                <div class="grid grid-cols-4 gap-4 mb-5">
                    <span class="text-base font-bold text-left w-full" />
                    <label
                        for="priceOnMonth"
                        class="text-base font-bold">
                        {{ $t('labels.ticketsTrip') }}
                        <span
                            v-if="!disabled"
                            class="text-error-500 ml-1">
                            *
                        </span>
                    </label>
                    <label
                        for="priceOnMonth"
                        class="text-base font-bold">
                        {{ $t('labels.ticketsMonth') }}
                    </label>
                    <span class="text-base font-bold">
                        {{ $t('labels.ticketsQuarter') }}
                    </span>
                </div>
            </div>
            <template v-if="Object.keys(tollStationsPrices.vehicle).length">
                <div
                    v-for="(item, index) in tollStationStructure?.vehicle"
                    :key="item"
                    v-loading="!visible">
                    <div class="grid grid-cols-4 gap-4 mb-4">
                        <span class="text-base font-bold text-left w-full">
                            {{ getLabelVehicle(item) }}
                        </span>
                        <hts-form-input
                            :key="`vehicle_trip${index}`"
                            v-model="tollStationsPrices.vehicle[item.id].trip"
                            class="w-full"
                            :color="props.color"
                            type="number"
                            :name="`vehicle_trip${index}`"
                            :prop="`vehicle_trip${index}`"
                            :readonly="disabled" />
                        <hts-form-input
                            :key="`vehicle_month${index}`"
                            v-model="tollStationsPrices.vehicle[item.id].month"
                            class="w-full"
                            :color="props.color"
                            type="number"
                            :name="`vehicle_month${index}`"
                            :prop="`vehicle_month${index}`"
                            :readonly="disabled" />
                        <hts-form-input
                            :key="`vehicle_quarter${index}`"
                            v-model="tollStationsPrices.vehicle[item.id].quarter"
                            class="w-full"
                            :color="props.color"
                            type="number"
                            :name="`vehicle_quarter${index}`"
                            :prop="`vehicle_quarter${index}`"
                            :readonly="disabled" />
                    </div>
                </div>
            </template>
            <template v-if="Object.keys(tollStationsPrices.cont).length">
                <div
                    v-for="(item, index) in tollStationStructure?.cont"
                    :key="item">
                    <div class="grid grid-cols-4 gap-4 mb-4">
                        <span class="text-base font-bold text-left w-full">
                            {{ getLabelCont(item) }}
                        </span>
                        <hts-form-input
                            :key="`cont_trip${index}`"
                            v-model="tollStationsPrices.cont[item].trip"
                            class="w-full"
                            :color="props.color"
                            type="number"
                            :name="`cont_trip${index}`"
                            :prop="`cont_trip${index}`"
                            :readonly="disabled" />
                        <hts-form-input
                            :key="`cont_month${index}`"
                            v-model="tollStationsPrices.cont[item].month"
                            class="w-full"
                            :color="props.color"
                            type="number"
                            :name="`cont_month${index}`"
                            :prop="`cont_month${index}`"
                            :readonly="disabled" />
                        <hts-form-input
                            :key="`cont_quarter${index}`"
                            v-model="tollStationsPrices.cont[item].quarter"
                            class="w-full"
                            :color="props.color"
                            type="number"
                            :name="`cont_quarter${index}`"
                            :prop="`cont_quarter${index}`"
                            :readonly="disabled" />
                    </div>
                </div>
            </template>
        </template>
    </hts-modal>
</template>
<script setup lang="ts">
import { isRef } from '@vue/reactivity'
import dataForm from '../imports/model'
import { $store } from '#imports'
const { $formatters } = useNuxtApp()
const { $disableReactive } = useNuxtApp()
const store = $store()
const tollStationStructure: any = store.data.tollStationStructure

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: Number,
    default: 0
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  },
  tollStationData: {
    type: Object,
    default: () => {}
  },
  disabled: {
    type: Boolean,
    default: false
  }
})
const emits = defineEmits(['update'])
const visible = ref(false)
const tollStationInformation = reactive({ ...dataForm.initValue.tollStationInformation })
const handleSubmit = () => {
  const vehiclePricesData = tollStationsPrices.value.vehicle

  for (const item in vehiclePricesData) {
    if (typeof vehiclePricesData[item].trip !== 'number') {
      process.client && $toast().error($t('labels.pleaseEnterTripTickets'))
      return
    }
  }

  const contPricesData = tollStationsPrices.value.cont

  for (const item in contPricesData) {
    if (typeof contPricesData[item].trip !== 'number') {
      process.client && $toast().error($t('labels.pleaseEnterTripTickets'))
      return
    }
  }

  emits('update', $disableReactive(tollStationsPrices.value))
  resetFields()
  handleClose()
}
const tollStationsPrices: any = ref({
  vehicle: {} as { [key: string]: any },
  cont: {} as { [key: string]: any }
})

const open = (tollStationsPricesData: any) => {
  visible.value = true
  tollStationsPrices.value = $disableReactive(tollStationsPricesData)
}
const handleClose = () => {
  visible.value = false
}

const resetFields = () => {
  tollStationsPrices.value = {
    vehicle: {} as { [key: string]: any },
    cont: {} as { [key: string]: any }
  }
}

const getLabelVehicle = (item: any) => {
  const { from, to } = item
  if (to) {
    return `Xe có tải trọng: ${String($formatters.formatNumber(from))} - ${String($formatters.formatNumber(to))} (kg)`
  } else {
    return `Xe có tải trọng trên ${String($formatters.formatNumber(from))} (kg)`
  }
}

const getLabelCont = (item: number) => {
  return `Container ${String(item)} feet`
}

defineExpose({ open })
</script>
